import './applicantPreviousEmploymentPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const {
    isSyncingIncome,
    previousIncomeEmployerName,
    previousIncomeEmploymentType,
    previousIncomeGrossAnnual,
    previousIncomeJobTitle,
    syncingIncomeFailedMessage,
    setPreviousIncomeEmployerName,
    setPreviousIncomeEmploymentType,
    setPreviousIncomeGrossAnnual,
    setPreviousIncomeJobTitle,
    syncIncome,
  } = useApplicantStore();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, touchedFields, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      previousEmployerNameValue: previousIncomeEmployerName,
      previousEmploymentTypeValue: previousIncomeEmploymentType,
      previousGrossAnnualIncomeValue: inputEventHandler.currency.formatter(
        previousIncomeGrossAnnual,
      ),
      previousJobTitleValue: previousIncomeJobTitle,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const shouldShowEmployedFields = employedTypes.includes(
    watch('previousEmploymentTypeValue'),
  );

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),

    onChange: (): void => {
      // the employmentType selection determines whether the employerName and jobTitle fields are rendered.
      // because the fields are conditionally required, we only revalidate the necessary form fields.
      const fieldsToRevalidate = shouldShowEmployedFields
        ? ([
            'previousEmployerNameValue',
            'previousJobTitleValue',
            'previousGrossAnnualIncomeValue',
          ] as const)
        : (['previousGrossAnnualIncomeValue'] as const);

      // only revalidate the fields that have been touched by the user as we normally validate onBlur.
      // this is to prevent the form from showing errors before the user has interacted with the form.
      fieldsToRevalidate
        .filter((field): boolean => touchedFields[field])
        .forEach((field): void => {
          trigger(field);
        });
    },
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      previousEmploymentTypeValue,
      previousEmployerNameValue,
      previousJobTitleValue,
      previousGrossAnnualIncomeValue,
    } = values;

    if (
      !previousEmploymentTypeValue ||
      (shouldShowEmployedFields && !previousEmployerNameValue) ||
      (shouldShowEmployedFields && !previousJobTitleValue) ||
      !previousGrossAnnualIncomeValue
    ) {
      return;
    }

    setPreviousIncomeEmploymentType(previousEmploymentTypeValue);
    setPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);

    if (shouldShowEmployedFields) {
      setPreviousIncomeEmployerName(previousEmployerNameValue);
      setPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setPreviousIncomeEmployerName(null);
      setPreviousIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-previous-employment-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantPreviousEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.applicantPreviousEmployment.form.h1}
              >
                {t('ApplicantPreviousEmploymentPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.applicantPreviousEmployment.form.h2}
              >
                {t('ApplicantPreviousEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantPreviousEmployment.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.applicantPreviousEmployment.form
                        .employmentTypeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantPreviousEmployment.form
                        .employmentTypeSelect
                    }
                    label={t('ApplicantPreviousEmploymentPage.employmentType')}
                    name="previousEmploymentTypeValue"
                    options={previousEmploymentTypeOptions}
                    rules={employmentTypeValidation}
                  />
                </div>

                {shouldShowEmployedFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'previousEmployerNameValue',
                        employerNameValidation,
                      )}
                      error={!isEmpty(errors.previousEmployerNameValue)}
                      label={t('ApplicantPreviousEmploymentPage.employerName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantPreviousEmployment.form
                              .employerNameInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.previousEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantPreviousEmployment.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.previousEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {shouldShowEmployedFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('previousJobTitleValue', jobTitleValidation)}
                      error={!isEmpty(errors.previousJobTitleValue)}
                      label={t('ApplicantPreviousEmploymentPage.jobTitle')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantPreviousEmployment.form
                              .jobTitleInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.previousJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantPreviousEmployment.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.previousJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'previousGrossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    error={!isEmpty(errors.previousGrossAnnualIncomeValue)}
                    label={t(
                      'ApplicantPreviousEmploymentPage.grossAnnualIncome',
                    )}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantPreviousEmployment.form
                            .grossAnnualIncomeInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'previousGrossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'previousGrossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'previousGrossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.previousGrossAnnualIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantPreviousEmployment.form
                          .grossAnnualIncomeInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.previousGrossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.applicantPreviousEmployment.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantPreviousEmploymentPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.applicantPreviousEmployment.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantPreviousEmploymentPage';
